/**
 * useCEPLookup Hook
 * Custom hook for CEP lookup with ViaCEP integration
 */

import { useState, useCallback } from 'react';
import { fetchAddressByCEP, AddressData } from '../utils/cepUtils';
import { useSnackbar } from 'notistack';

interface UseCEPLookupResult {
  loading: boolean;
  error: string | null;
  addressData: AddressData | null;
  lookupCEP: (cep: string) => Promise<AddressData | null>;
  clearAddress: () => void;
}

export const useCEPLookup = (): UseCEPLookupResult => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [addressData, setAddressData] = useState<AddressData | null>(null);

  const lookupCEP = useCallback(async (cep: string): Promise<AddressData | null> => {
    // Clean CEP
    const cleanCEP = cep.replace(/\D/g, '');
    
    if (cleanCEP.length !== 8) {
      const errorMsg = 'CEP deve ter 8 dígitos';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'warning' });
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await fetchAddressByCEP(cleanCEP);
      
      if (!data) {
        const errorMsg = 'CEP não encontrado';
        setError(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'warning' });
        return null;
      }
      
      setAddressData(data);
      enqueueSnackbar('Endereço encontrado com sucesso', { variant: 'success' });
      
      return data;
    } catch (err: any) {
      const errorMsg = 'Erro ao buscar CEP';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const clearAddress = useCallback(() => {
    setAddressData(null);
    setError(null);
  }, []);

  return {
    loading,
    error,
    addressData,
    lookupCEP,
    clearAddress
  };
};
